import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { Modal } from '@domgen/base-design-system-2.0/molecules/Modal';
import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { usePostHog, useFeatureFlagEnabled } from '@posthog/react';
import { useState, useEffect } from 'react';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { gaPageView, gaEvent, gaECommerceEvent } from '@domgen/shared/services/ga/ga';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useAppState } from '../../../store/selectors/useAppState';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { BenefitsTableAccordion } from '../components/BenefitsAccordionTable/BenefitsAccordionTable';
import { OfferPanel } from '../components/OfferPanel';
import { benefitsAccordionTableContent } from '../constants/benefitsAccordionTableContent';
import { quoteModalContent } from '../constants/quoteModalContent';
import { useQuote } from '../../../store/selectors/useQuote';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { getDiscountDisplayData } from '../../../utils/discounts/getDiscountDisplayData';
import { store } from '../../../store/store';
import { GiftIcon } from '../../../assets/icons/GiftIcon';
import { StoreEvent } from '../../../enums/storeEvent';
import { ChooseApplianceCareArticle } from '../components/ChooseApplianceCareArticle';
import { chooseApplianceCareArticles } from '../constants/chooseApplianceCareArticles';
import { QuoteTermsAndConditionsPanel } from '../components/QuoteTermsAndConditionsPanel';

const styles = {
  applianceCareTermsContainer: 'col-span-12 flex justify-center mb-space-400',
  container: 'flex flex-col items-center bg-bg-main',
  panel: 'grid grid-cols-12 max-w-206.25 p-space-500  gap-space-600 w-full bg-neutral-0',
  headerRow: 'col-span-12 desktop:mr-space-400 desktop:mb-space-600',
  chooseApplianceCareContainer:
    'w-screen relative left-1/2 right-1/2 -translate-x-1/2 flex flex-col justify-center items-center gap-space-500 px-space-700 py-space-800 mb-space-600 bg-neutral-0',
  chooseApplianceCareArticles:
    'flex flex-col desktop:flex-row justify-center items-center gap-space-500 desktop:mx-space-400 ',
  offerPanelModal: 'desktop:min-w-3xl w-full flex justify-center',
  buttonLayout: 'col-span-12 flex flex-col gap-space-500 desktop:flex-row desktop:justify-center',
  buttonLayoutModal:
    'flex flex-col w-full justify-center gap-space-500 desktop:flex-row desktop:items-center',
  buttonLayoutSideDraw: 'w-full flex flex-col gap-space-500 p-space-500 ',
};

export function QuotePageSubs({ brand }: { brand: string }) {
  const quote = useQuote();
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [displayModal, setDisplayModal] = useState(false);
  const posthog = usePostHog();
  const quoteData = quote?.data;
  const gaEventData = useGaEventData() || {};
  const offerPrice = gaEventData?.netFirstPaymentAmount;
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const appSessionID = session?.id;

  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;

  if (!quoteData || !quoteData.offer || !quoteData.offer.contracts) {
    store.send({ type: StoreEvent.QUOTE_ERROR });
    return null;
  }

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'olr-protection-details',
      offerPrice,
      quoteId: quoteData.offer.offerReference,
    });
  }, []);

  const discountTerms = getDiscountDisplayData(quoteData);

  const toggleModal = () => {
    capturePostHogEvent(posthog, 'protection-details-page-register-only-cta-clicked', store.get());

    setDisplayModal((prevDisplayModal) => {
      // Fire gaEvent only when the modal is being opened
      if (!prevDisplayModal) {
        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails',
          eventLabel: 'No, I want to register with warranty only',
          pagename: 'olr-protection-details',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          offerPrice,
          quoteId: quoteData.offer.offerReference,
        });

        gaPageView({
          event: 'spa-pageview',
          pagename: 'olr-protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          offerPrice,
          applianceName,
          applianceCategory,
          appSessionID,
          quoteId: quoteData.offer.offerReference,
        });

        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails_popup_upsell',
          eventLabel: 'popup_presented',
          pagename: 'olr-protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          offerPrice,
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          quoteId: quoteData.offer.offerReference,
        });
      }
      return !prevDisplayModal;
    });
  };

  const registerOnly = () => {
    store.send({ type: StoreEvent.REG_ONLY });
    capturePostHogEvent(posthog, 'protection-details-modal-register-only-cta-clicked', store.get());
    //Register only event only continues from the modal
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details_popup_upsell',
      eventLabel: 'No, register with warranty only',
      pagename: 'olr-protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      quoteId: quoteData.offer.offerReference,
    });

    setDisplayModal(false);
  };

  const continueToCheckout = () => {
    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: displayModal },
    });

    capturePostHogEvent(posthog, 'protection-details-page-monthly-upsell-cta-clicked', store.get());

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: displayModal ? 'protectionDetails_popup_upsell' : 'protectionDetails',
      eventLabel: 'Yes, I want this protection',
      pagename: displayModal ? 'olr-protection-details-popup' : 'olr-protection-details',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      quoteId: quoteData.offer.offerReference,
    });

    gaECommerceEvent({
      event: 'add_to_cart',
      pagename: displayModal ? 'olr-protection-details-popup' : 'olr-protection-details',
      regSystemCode: systemCode,
      version: 'WiB',
      currency: 'USD',
      appSessionID,
      quoteId: quoteData.offer.offerReference,
      ecommerce: {
        items: [
          {
            item_brand: brand,
            item_category: applianceCategory,
            item_appliance_type: applianceName,
            item_appliance_purchase_cost: Number(
              formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            ),
            item_appliance_purchase_date: formatLocalisedDate(
              parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
              'en-US',
            ),
            item_appliance_warranty: true,
            item_name: `${brand} ${applianceName}`,
            price: offerPrice,
            quantity: 1,
            quoteId: quoteData.offer.offerReference,
          },
        ],
      },
    });
  };

  const banner = () => {
    const discountTerm = discountTerms.discountTerm;
    const periodOfCover = discountTerms.periodOfCover;
    const discountPercentage = discountTerms.discountPercentage;
    const achMarker = discountTerms.discountPaymentType === 'ach' ? '*' : '';

    const formatBannerText = () => {
      if (show2MonthsPromotion) {
        return "Our Lowest Price Ever is Back - Don't Miss Out!";
      }

      if (discountTerms.discountPercentage && discountTerm === periodOfCover) {
        return `Online Exclusive Offer - Get ${discountPercentage}% off${achMarker}!`;
      }

      if (discountPercentage && discountTerm && discountTerm < periodOfCover) {
        return `Online Exclusive Offer - Get ${discountPercentage}% off for first ${discountTerm} months${achMarker}!`;
      }

      return <>ONLINE OFFER</>;
    };

    return (
      <div className="col-span-12" role="banner">
        <div className="flex flex-row w-full items-center justify-center py-space-400 px-space-600 rounded-radius-200 bg-success-100 text-center text-success-600">
          <GiftIcon size={24} />
          <p className="ml-space-400 text-body-large-strong text-success-600">
            {formatBannerText()}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.panel}>
          {banner()}
          <div className={styles.headerRow}>
            <h1 className="mb-space-400 text-h1">
              Never buy another {applianceName} again with {brand} Care Plus
            </h1>
            <p className="text-body-large">
              *Enhanced protection you can rely on - if we can't fix it, we'll replace it with a
              brand new one.
            </p>
          </div>

          <div className="col-span-12 ">
            <BenefitsTableAccordion panels={benefitsAccordionTableContent} />
          </div>

          <div className="col-span-12  md:col-span-6">
            <div className="bg-bg-main px-space-600 py-space-500 rounded-radius-200">
              <QuoteTermsAndConditionsPanel brand={`${brand} Care Plus`} />
            </div>
          </div>

          <div className="col-span-12  md:col-span-6 w-full flex justify-center items-center">
            <OfferPanel
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
              brand={brand}
            />
          </div>

          <div className={styles.buttonLayout}>
            <Button
              variant="outline"
              onClick={() => {
                toggleModal();
              }}
            >
              No, register only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                continueToCheckout();
              }}
            >
              Yes, protect my appliance!
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.chooseApplianceCareContainer}>
        <h2 className="text-center text-text-headline text-h2">Why choose {brand} Care Plus?</h2>
        <div className={styles.chooseApplianceCareArticles}>
          {chooseApplianceCareArticles.map((article, index) => (
            <ChooseApplianceCareArticle
              icon={article.icon}
              description={article.description}
              key={index}
            />
          ))}
        </div>
      </div>

      <Modal isOpen={displayModal} onClose={toggleModal}>
        <h1 className="text-h5  mb-space-400">{quoteModalContent.title}</h1>
        <p className="mb-space-400">{quoteModalContent.description}</p>
        <p className="text-body-strong mb-space-200">{quoteModalContent.benefits.heading}</p>
        <ul className="list-none mb-4 text-left">
          {quoteModalContent.benefits.benefitsList.map((benefit, index) => (
            <li key={index} className="flex items-center mb-2">
              <span className="mr-2">{<benefit.icon />}</span>
              <span>{benefit.text}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-center gap-space-400">
          <div className={styles.offerPanelModal}>
            <OfferPanel
              brand={brand}
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
              alignment="center"
              variant="primary"
              displayIcon="none"
            />
          </div>

          <div className={styles.buttonLayoutModal}>
            <Button
              variant="outline"
              onClick={() => {
                registerOnly();
              }}
            >
              No thanks, register only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                continueToCheckout();
              }}
            >
              Register with extra coverage
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
